import React, { useState, forwardRef, useImperativeHandle, useEffect } from 'react';
import OlcButton from '~/components/General/OlcButton';
import OlcFormModal from '~/components/General/OlcFormModal';

// Third Party Libraries
import DatePicker from '~/components/General/OlcDatePicker';

// Hooks
import { useDispatch, useSelector } from 'react-redux';

// Actions
import {
  CLEAR_ADMIN_ORDERS_FILTERS,
  SET_ADMIN_ORDERS_FILTERS,
} from '~/redux/actions/action-types.js';
import { failure } from '~/redux/actions/snackbar-actions';
import { fetchAdminUsers } from '~/redux/actions/admin-users-actions.js';

// Utils
import { MESSAGES } from '~/utils/message';
import {
  dateProtector,
  handleRawChangeForDatePicker,
  formatDateOnly,
  parseLocalDateOnly,
} from '~/utils/date-format.js';
import { DATE_REGEX, DEFAULT_PAGINATION } from '~/utils/constants';

// Components
import MultiSelect from '~/components/General/MultiSelectNew';
import OlcDropdownSearch from '~/components/General/OlcDropdownSearch';

// Mui components
import Typography from '@mui/material/Typography';
import { Box, Grid, Input, InputLabel } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

// Styles
import './styles.scss';

const AdvanceFilters = forwardRef((props, ref) => {
  const { handleCloseFilter, open, updatePagination } = props;
  const [selectedCreatedBy, setSelectedCreatedBy] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState([]);
  const [selectedSource, setSelectedSource] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState([]);
  const [selectedPayment, setSelectedPayment] = useState([]);
  const [isDatepickerOpen, setIsDatepickerOpen] = useState({
    created: false,
    scheduled: false,
  });
  const [createdDate, setCreatedDate] = useState([null, null]);
  const [scheduledDate, setScheduleDate] = useState([null, null]);
  const [mailedDate, setMailedDate] = useState([null, null]);
  const [startDateCreated, endDateCreated] = createdDate;
  const [startDateScheduled, endDateScheduled] = scheduledDate;
  const [startDateMailed, endDateMailed] = mailedDate;
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [address, setAddress] = useState(null);

  const dispatch = useDispatch();

  const createdBy = useSelector((state) => state.adminUsersReducers.users);
  // Ensure createdBy is defined before mapping
  const createdByOptions = createdBy
    ? createdBy.map((user) => ({
        title: `${user.name} (${user.email})`,
        id: user.id,
      }))
    : [];

  const orderStatus = useSelector((state) => state.adminOrdersReducers.filterslabels.orderStatus);

  const paymentStatus = useSelector(
    (state) => state.adminOrdersReducers.filterslabels.paymentStatus
  );

  const productType = useSelector((state) => state.adminOrdersReducers.filterslabels.productType);

  const source = useSelector((state) => state.adminOrdersReducers.filterslabels.source);

  const perPage = useSelector(
    (state) => state.adminOrdersReducers.perPage || DEFAULT_PAGINATION.pageSize
  );

  const filters = useSelector((state) => state.adminOrdersReducers.filters);

  const onClose = () => {
    clearFilters(true);
  };

  const clearFilters = (persist = false) => {
    setSelectedCreatedBy(null);
    setSelectedProduct([]);
    setSelectedSource([]);
    setSelectedOrder([]);
    setSelectedPayment([]);
    setScheduleDate([null, null]);
    setCreatedDate([null, null]);
    setMailedDate([null, null]);
    setAddress(null);
    !persist ? dispatch({ type: CLEAR_ADMIN_ORDERS_FILTERS }) : undefined;
    handleCloseFilter();
  };

  const applyFilters = () => {
    const dateRangeCreate =
      startDateCreated && endDateCreated
        ? {
            startDate: formatDateOnly(startDateCreated),
            endDate: formatDateOnly(endDateCreated),
          }
        : null;

    if (startDateCreated && endDateCreated === null) {
      dispatch(failure(MESSAGES.ORDERS.END_DATE_REQUIRED));
      return;
    }

    const dateRangescheduled =
      startDateScheduled && endDateScheduled
        ? {
            startDate: formatDateOnly(startDateScheduled),
            endDate: formatDateOnly(endDateScheduled),
          }
        : null;

    if (startDateScheduled && endDateScheduled === null) {
      dispatch(failure(MESSAGES.ORDERS.END_DATE_REQUIRED));
      return;
    }

    const dateRangeMailed =
      startDateMailed && endDateMailed
        ? {
            startDate: formatDateOnly(startDateMailed),
            endDate: formatDateOnly(endDateMailed),
          }
        : null;

    if (startDateMailed && endDateMailed === null) {
      dispatch(failure(MESSAGES.ORDERS.END_DATE_REQUIRED));
      return;
    }

    let payload = {
      applyFilters: true,
      filters: {
        createdBy:
          selectedCreatedBy && selectedCreatedBy?.id ? [Number(selectedCreatedBy?.id)] : [],
        createdByUser: selectedCreatedBy ?? null,
        productType: selectedProduct,
        source: selectedSource,
        orderStatus: selectedOrder,
        paymentStatus: selectedPayment,
        createdAt: dateRangeCreate,
        scheduledDate: dateRangescheduled,
        mailedDate: dateRangeMailed,
        ...(address ? { address } : {}),
      },
    };

    const filtersWithValues = Object.keys(payload.filters).filter((key) => {
      if (key === 'createdByUser') return false;
      const value = payload.filters[key];
      return Array.isArray(value) ? value.length > 0 : value !== null;
    });

    payload.filterSize = filtersWithValues.length;

    updatePagination({
      pageSize: perPage,
      page: 1,
    });

    if (filtersWithValues.length <= 0) {
      dispatch(failure(MESSAGES.ORDERS.FILTERS_REQUIRED));
      return;
    }

    dispatch({ type: SET_ADMIN_ORDERS_FILTERS, payload: payload });
    onClose();
  };

  const searching = async () => {
    setLoading(true);
    await dispatch(fetchAdminUsers({ search }));
    setLoading(false);
  };

  useImperativeHandle(ref, () => ({
    clearFilters() {
      clearFilters();
    },
  }));

  useEffect(() => {
    if (Object.values(filters).length) {
      if (filters.createdBy?.length && createdBy?.length) {
        const selectedUser = createdBy.find((user) => user.id == filters.createdBy[0]);
        setSelectedCreatedBy(
          selectedUser
            ? {
                id: selectedUser.id,
                title: `${selectedUser.name} (${selectedUser.email})`,
              }
            : null
        );
      }
      if (filters.createdByUser) {
        setSelectedCreatedBy(filters.createdByUser);
      }
      setSelectedProduct(filters.productType);
      setSelectedSource(filters.source);
      setSelectedOrder(filters.orderStatus);
      setSelectedPayment(filters.paymentStatus);
      setAddress(filters.address);
      filters.createdAt
        ? setCreatedDate([
            parseLocalDateOnly(filters?.createdAt?.startDate),
            parseLocalDateOnly(filters?.createdAt?.endDate),
          ])
        : undefined;
      filters.scheduledDate
        ? setScheduleDate([
            parseLocalDateOnly(filters?.scheduledDate?.startDate),
            parseLocalDateOnly(filters?.scheduledDate?.endDate),
          ])
        : undefined;
      filters.mailedDate
        ? setMailedDate([
            parseLocalDateOnly(filters?.mailedDate?.startDate),
            parseLocalDateOnly(filters?.mailedDate?.endDate),
          ])
        : undefined;
    }
    if (open) {
      dispatch(fetchAdminUsers());
    }
  }, [open]);

  useEffect(() => {
    if (open) {
      searching();
    }
  }, [search]);

  return (
    <OlcFormModal
      open={open}
      onClose={onClose}
      title={MESSAGES.ADMIN_DASHBOARD.ORDERS.ADVANCE_FILTERS.HEADING}
      className="orderfilterModalWrapper olcAdminFilterModal"
      contentClassName="filterModalContent"
      footer={
        <>
          <OlcButton variant="secondary" onClick={() => clearFilters()}>
            {MESSAGES.ADMIN_DASHBOARD.ORDERS.ADVANCE_FILTERS.CANCEL_BUTTON}
          </OlcButton>
          <OlcButton variant="primary" onClick={applyFilters}>
            {MESSAGES.ADMIN_DASHBOARD.ORDERS.ADVANCE_FILTERS.SUBMIT_BUTTON}
          </OlcButton>
        </>
      }
    >
      <Grid container className="adminOrdersFilterGrid">
        <Grid item lg={6} md={6} sm={12} xs={12}>
          <Box className="inputWrapper">
            <Typography>
              {MESSAGES.ADMIN_DASHBOARD.ORDERS.ADVANCE_FILTERS.PRODUCT_TYPE_LABEL}
            </Typography>
            <MultiSelect
              options={productType}
              selectedValue={selectedProduct}
              setSelectedValue={setSelectedProduct}
              placeHolderText={
                MESSAGES.ADMIN_DASHBOARD.ORDERS.ADVANCE_FILTERS.PRODUCT_TYPE_PLACEHOLDER
              }
              noOptionText={MESSAGES.ADMIN_DASHBOARD.ORDERS.ADVANCE_FILTERS.NO_PRODUCT_TYPE}
              clearable={true}
            />
          </Box>
        </Grid>
        <Grid item lg={6} md={6} sm={12} xs={12}>
          <Box className="inputWrapper">
            <Typography>{MESSAGES.ADMIN_DASHBOARD.ORDERS.ADVANCE_FILTERS.SOURCE_LABEL}</Typography>
            <MultiSelect
              options={source}
              selectedValue={selectedSource}
              setSelectedValue={setSelectedSource}
              placeHolderText={MESSAGES.ADMIN_DASHBOARD.ORDERS.ADVANCE_FILTERS.SOURCE_PLACEHOLDER}
              noOptionText={MESSAGES.ADMIN_DASHBOARD.ORDERS.ADVANCE_FILTERS.NO_SOURCE}
              clearable={true}
            />
          </Box>
        </Grid>
        <Grid item lg={6} md={6} sm={12} xs={12}>
          <Box className="inputWrapper">
            <Typography>
              {MESSAGES.ADMIN_DASHBOARD.ORDERS.ADVANCE_FILTERS.ORDER_STATUS_LABEL}
            </Typography>
            <MultiSelect
              options={orderStatus}
              selectedValue={selectedOrder}
              setSelectedValue={setSelectedOrder}
              placeHolderText={
                MESSAGES.ADMIN_DASHBOARD.ORDERS.ADVANCE_FILTERS.ORDER_STATUS_PLACEHOLDER
              }
              noOptionText={MESSAGES.ADMIN_DASHBOARD.ORDERS.ADVANCE_FILTERS.NO_ORDER_STATUS}
              clearable={true}
            />
          </Box>
        </Grid>
        <Grid item lg={6} md={6} sm={12} xs={12}>
          <Box className="inputWrapper">
            <Typography>
              {MESSAGES.ADMIN_DASHBOARD.ORDERS.ADVANCE_FILTERS.PAYMENT_STATUS_LABEL}
            </Typography>
            <MultiSelect
              options={paymentStatus}
              selectedValue={selectedPayment}
              setSelectedValue={setSelectedPayment}
              placeHolderText={
                MESSAGES.ADMIN_DASHBOARD.ORDERS.ADVANCE_FILTERS.PAYMENT_STATUS_PLACEHOLDER
              }
              noOptionText={MESSAGES.ADMIN_DASHBOARD.ORDERS.ADVANCE_FILTERS.NO_PAYMENT_STATUS}
              clearable={true}
            />
          </Box>
        </Grid>
        <Grid item lg={6} md={6} sm={12} xs={12}>
          <Box className="mailedDate">
            <Box className="selectDate">
              <InputLabel className="dateLabel">
                {MESSAGES.ADMIN_DASHBOARD.ORDERS.ADVANCE_FILTERS.CREATED_DATE_LABEL}
              </InputLabel>
              <KeyboardArrowDownIcon
                sx={{
                  color: isDatepickerOpen.created ? '#ED5C2F' : 'inherit',
                }}
              />
              <DatePicker
                onCalendarClose={() =>
                  setIsDatepickerOpen((prevState) => ({
                    ...prevState,
                    created: false,
                  }))
                }
                selectsRange={true}
                startDate={startDateCreated}
                endDate={endDateCreated}
                minDate={null}
                onChange={(update) => {
                  dateProtector(update);
                  setCreatedDate(update);
                }}
                onChangeRaw={(e) => {
                  if (e.target.value === '' || DATE_REGEX.test(e.target.value)) {
                    handleRawChangeForDatePicker(e, setCreatedDate);
                  } else {
                    e.preventDefault();
                  }
                }}
                onFocus={() =>
                  setIsDatepickerOpen((prevState) => ({
                    ...prevState,
                    created: true,
                  }))
                }
                onBlur={(e) => {
                  if (e.target.value == '') {
                    setCreatedDate([null, null]);
                  }
                  setIsDatepickerOpen((prevState) => ({
                    ...prevState,
                    created: false,
                  }));
                }}
                placeholderText={MESSAGES.ADMIN_DASHBOARD.ORDERS.ADVANCE_FILTERS.DATE_PLACEHOLDER}
                isClearable
              />
            </Box>
          </Box>
        </Grid>
        <Grid item lg={6} md={6} sm={12} xs={12}>
          <Box className="mailedDate">
            <Box className="selectDate">
              <InputLabel className="dateLabel">
                {MESSAGES.ADMIN_DASHBOARD.ORDERS.ADVANCE_FILTERS.SCHEDULED_DATE_LABEL}
              </InputLabel>
              <KeyboardArrowDownIcon
                sx={{
                  color: isDatepickerOpen.scheduled ? '#ED5C2F' : 'inherit',
                }}
              />
              <DatePicker
                onCalendarClose={() =>
                  setIsDatepickerOpen((prevState) => ({
                    ...prevState,
                    scheduled: false,
                  }))
                }
                selectsRange={true}
                startDate={startDateScheduled}
                endDate={endDateScheduled}
                onChange={(update) => {
                  dateProtector(update);
                  setScheduleDate(update);
                }}
                minDate={null}
                onChangeRaw={(e) => {
                  if (e.target.value === '' || DATE_REGEX.test(e.target.value)) {
                    handleRawChangeForDatePicker(e, setScheduleDate);
                  } else {
                    e.preventDefault();
                  }
                }}
                onFocus={() =>
                  setIsDatepickerOpen((prevState) => ({
                    ...prevState,
                    scheduled: true,
                  }))
                }
                onBlur={(e) => {
                  if (e.target.value == '') {
                    setScheduleDate([null, null]);
                  }
                  setIsDatepickerOpen((prevState) => ({
                    ...prevState,
                    scheduled: false,
                  }));
                }}
                placeholderText={MESSAGES.ADMIN_DASHBOARD.ORDERS.ADVANCE_FILTERS.DATE_PLACEHOLDER}
                isClearable
              />
            </Box>
          </Box>
        </Grid>
        <Grid item lg={6} md={6} sm={12} xs={12}>
          <Box className="inputWrapper">
            <Typography>{MESSAGES.ORDERS.ADVANCE_FILTERS.ADDRESS_LABEL}</Typography>
            <Input
              className="createdByInput"
              value={address}
              name="searchAddress"
              autoComplete="off"
              onChange={(e) => setAddress(e.target.value)}
              type="text"
              placeholder={MESSAGES.ORDERS.ADVANCE_FILTERS.ADDRESS_PLACEHOLDER}
            />
          </Box>
        </Grid>
        <Grid item lg={6} md={6} sm={12} xs={12}>
          <Box className="mailedDate">
            <Box className="selectDate">
              <InputLabel className="dateLabel">
                {MESSAGES.ADMIN_DASHBOARD.ORDERS.ADVANCE_FILTERS.MAILED_DATE_LABEL}
              </InputLabel>
              <KeyboardArrowDownIcon
                sx={{
                  color: isDatepickerOpen.scheduled ? '#ED5C2F' : 'inherit',
                }}
              />
              <DatePicker
                onCalendarClose={() =>
                  setIsDatepickerOpen((prevState) => ({
                    ...prevState,
                    scheduled: false,
                  }))
                }
                selectsRange={true}
                startDate={startDateMailed}
                endDate={endDateMailed}
                onChange={(update) => {
                  dateProtector(update);
                  setMailedDate(update);
                }}
                minDate={null}
                onChangeRaw={(e) => {
                  if (e.target.value === '' || DATE_REGEX.test(e.target.value)) {
                    handleRawChangeForDatePicker(e, setMailedDate);
                  } else {
                    e.preventDefault();
                  }
                }}
                onFocus={() =>
                  setIsDatepickerOpen((prevState) => ({
                    ...prevState,
                    scheduled: true,
                  }))
                }
                onBlur={(e) => {
                  if (e.target.value == '') {
                    setMailedDate([null, null]);
                  }
                  setIsDatepickerOpen((prevState) => ({
                    ...prevState,
                    scheduled: false,
                  }));
                }}
                placeholderText={MESSAGES.ADMIN_DASHBOARD.ORDERS.ADVANCE_FILTERS.DATE_PLACEHOLDER}
                isClearable
              />
            </Box>
          </Box>
        </Grid>
        <Grid item lg={6} md={6} sm={12} xs={12}>
          <Box className="inputWrapper">
            <Typography>
              {MESSAGES.ADMIN_DASHBOARD.ORDERS.ADVANCE_FILTERS.CREATED_BY_LABEL}
            </Typography>
            <OlcDropdownSearch
              placeHolderText={
                MESSAGES.ADMIN_DASHBOARD.ORDERS.ADVANCE_FILTERS.CREATED_BY_PLACEHOLDER
              }
              searchFieldPlaceholderText="users"
              noOptionText="No users to show"
              options={createdByOptions}
              selectedValue={selectedCreatedBy}
              setSelectedValue={setSelectedCreatedBy}
              component="multipleIds"
              onSearchChange={setSearch}
              searchLoading={loading}
              clearable
            />
          </Box>
        </Grid>
      </Grid>
    </OlcFormModal>
  );
});

export default AdvanceFilters;
